import { Column, Columns } from '@dixa/lib-shared-habla';
import { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { analyticsApi } from 'apis/analytics';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './analytics.view.module.scss';
import { AnalyticsAdvancedInsightsTeaserModal } from './components/advancedInsights/components/teaserModal/advancedInsightsTeaserModal';
import { AnalyticsError } from './components/error/analyticsError';
import { AnalyticsLoading } from './components/loading/analyticsLoading';
import { AnalyticsPinPanelModal } from './components/panels/components/pin/analyticsPinPanelModal';
import { AnalyticsQuickViewModal } from './components/quickView/components/modal/quickViewModal';
import {
  AnalyticsSidenav,
  analyticsSidenavConfig,
} from './components/sidenav/analyticsSidenav';

export const AnalyticsView = (): JSX.Element => {
  const { dashboards, isLoading, isUninitialized, isError, refetch } =
    analyticsApi.useReadDashboardsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        dashboards: data?.data.dashboards,
      }),
    });

  const flags = useFeatureFlag(['analytics-custom-dashboards']);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  /** Renders the appropriate content based on the status of the API request. */
  const renderContent = (): ReactNode => {
    if (isUninitialized || isLoading) {
      return <AnalyticsLoading />;
    }

    // When the request failed or no dashboards were returned.
    if (isError || (dashboards && dashboards.length === 0)) {
      return (
        <AnalyticsError
          buttonProps={{ children: 'Try again', onClick: refetch }}
        />
      );
    }

    return <Outlet />;
  };

  return (
    <div className={styles.root}>
      <Columns>
        <Column
          size={isSmallScreen ? 0 : analyticsSidenavConfig.widthCollapsed}
        >
          <Column.Row size={1} noHorizontalPadding noVerticalPadding>
            <AnalyticsSidenav isExpandable={!isLoading} />
          </Column.Row>
        </Column>

        <Column>
          <Column.Row size={1} noHorizontalPadding noVerticalPadding>
            {renderContent()}
          </Column.Row>
        </Column>
      </Columns>

      <AnalyticsQuickViewModal />
      {flags.analyticsCustomDashboards && <AnalyticsPinPanelModal />}
      <AnalyticsAdvancedInsightsTeaserModal />
    </div>
  );
};
